import React, { memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { optionApi } from '@app/store';
import { OptionDTO } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { OptionForm } from './OptionForm';
import { OptionFormValues } from './option-form.helpers';
import { useToast, useTriptych, useUpdateCardData } from '@app/hooks';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store/slices/triptych.slice';
import { sansLesClesPriveesDeCopie, type ValeursDeCopie } from '../Duplication/cles-privees';

type Props = { copie?: ValeursDeCopie<OptionFormValues> };

// Le formulaire porte le useForm : il est remonté (clé) quand on passe d'une copie
// au vierge ou d'une copie à une autre, car react-hook-form ne relit
// `defaultValues` qu'au montage.
const OptionCreateForm = memo(({ copie }: Props) => {
  const toast = useToast();

  const triptych = useTriptych()

  const [create, { isLoading, error: apiErrors }] = optionApi.useCreateOneOptionMutation();

  const valeursVierges: OptionFormValues = {
    name: '',
    disabled: false,
    mandatory: false,
    freeQuantity: 0,
    maxChoiceCount: 0,
    products: [],
    position: 0
  };

  const defaultValues = copie?.copieDe ? copie : valeursVierges;

  const formMethods = useForm<OptionFormValues>({
    resolver: classValidatorResolver(OptionDTO),
    defaultValues,
  });

  // @ts-ignore
  useUpdateCardData(formMethods, ['name']);

  const onCreate = async (formValues: OptionFormValues) => {
    try {
      // `copieDe` ne sert qu'à ce panneau : l'option part en JSON, sans clé privée de copie.
      const option = await create(sansLesClesPriveesDeCopie(formValues)).unwrap();

      triptych.close({ mode: 'UPDATE', data: option });

      toast.success('Votre option a bien été ajoutée.')
    } catch {
      toast.error('Une erreur est survenue lors de la création de votre option.')
    }
  }

  return (
    <Panel.Content>

      <Form formMethods={formMethods} apiErrors={apiErrors}>

        <Panel.FormContainer>

          <OptionForm />

        </Panel.FormContainer>

        <Panel.Actions>
          <Button
            title="Retour"
            grow
            large
            bordered
            onPress={() => triptych.close()} />

          <Button
            loading={isLoading}
            title="Valider"
            onPress={formMethods.handleSubmit(onCreate)}
            grow
            large />
        </Panel.Actions>

      </Form>

    </Panel.Content>
  );
});

export const OptionCreate = memo(() => {
  // Dupliquer : la fiche ouvre ce panneau avec les valeurs de copie dans le triptyque.
  const copie = useAppSelector(triptychSelectors.selectData) as ValeursDeCopie<OptionFormValues> | undefined;

  return <OptionCreateForm key={copie?.copieDe ?? 'creation'} copie={copie?.copieDe ? copie : undefined} />;
});
